import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export default function Home({ blogs, setBlogs }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');

  // Filters
  const [sources, setSources] = useState([]); // [{id:"1", name:"yaper"}, ...]
  const [selectedSourceId, setSelectedSourceId] = useState('ALL'); // 'ALL' | "1" | "2" | "3"
  const [selectedPriority, setSelectedPriority] = useState('ALL'); // 'ALL' | '1' | '0'
  const [selectedType, setSelectedType] = useState('ALL'); // 'ALL' | 'blog' | 'static' | 'cc_blog'
  const [loading, setLoading] = useState(false);

  // Core fetcher (server-side filters: source_id + content_type)
  const fetchAllBlogs = async (sourceId = null, contentType = null) => {
    setLoading(true);
    try {
      const all = [];
      const seen = new Set();
      let page = 1;
      const MAX_PAGES = 400;

      while (page <= MAX_PAGES) {
        const ts = Date.now();
        let url = `https://app.yaper.co/api/v1/content_pages?page_number=${page}&_=${ts}`;
        if (sourceId) url += `&source_id=${sourceId}`;
        if (contentType) url += `&content_type=${contentType}`;

        const res = await fetch(url, { cache: 'no-store' });
        if (!res.ok) break;

        const json = await res.json();
        const data = Array.isArray(json?.data)
          ? json.data
          : Array.isArray(json)
            ? json
            : [];
        if (!data.length) break;

        for (const item of data) {
          const key = item?.id ?? `${page}-${Math.random()}`;
          if (!seen.has(key)) {
            seen.add(key);
            all.push(item);
          }
        }

        if (data.length < 10) break;
        page += 1;
      }

      all.sort((a, b) => {
        const da = new Date(a?.updated_at || a?.created_at || 0).getTime();
        const db = new Date(b?.updated_at || b?.created_at || 0).getTime();
        return db - da;
      });

      return all;
    } catch {
      return [];
    } finally {
      setLoading(false);
    }
  };

  const reloadCurrent = async () => {
    const source = selectedSourceId === 'ALL' ? null : selectedSourceId;
    const type = selectedType === 'ALL' ? null : selectedType;
    const all = await fetchAllBlogs(source, type);
    setBlogs(all);
  };

  // Initial load
  useEffect(() => {
    (async () => {
      const all = await fetchAllBlogs(null, null);
      setBlogs(all);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setBlogs]);

  // Reload when navigating back or URL key changes
  useEffect(() => {
    reloadCurrent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Load source options
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(
          'https://app.yaper.co/api/v1/content_pages/get_sources',
          { cache: 'no-store' }
        );
        if (!res.ok) throw new Error();
        const json = await res.json();

        let list = [];
        if (Array.isArray(json)) list = json;
        else if (Array.isArray(json?.data)) list = json.data;

        const normalized = list
          .map((s) => {
            const id = s?.id ?? s?.value ?? s?.source_id ?? null;
            const name =
              s?.name ??
              s?.label ??
              s?.title ??
              s?.source ??
              String(id ?? '').trim();
            return {
              id: id != null ? String(id) : null,
              name: String(name || '').trim(),
            };
          })
          .filter((x) => x.id && x.name);

        setSources(normalized);
      } catch {
        setSources([]);
      }
    })();
  }, []);

  // Re-fetch when source OR type changes
  useEffect(() => {
    reloadCurrent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSourceId, selectedType]);

  // ---------- UI filtering (search + priority) ----------
  const safeBlogs = Array.isArray(blogs) ? blogs : [];
  const norm = (v) =>
    String(v ?? '')
      .trim()
      .toLowerCase();
  const getBlogPriority = (b) => {
    const p =
      b?.priority ??
      b?.prio ??
      b?.importance ??
      b?.rank ??
      b?.meta_priority ??
      b?.meta?.priority ??
      '';
    const num = Number(p);
    if (Number.isFinite(num) && (num === 0 || num === 1)) return String(num);
    const s = norm(p);
    if (s === '1' || s === 'true' || s === 'yes') return '1';
    if (s === '0' || s === 'false' || s === 'no') return '0';
    return '';
  };

  const filteredBlogs = safeBlogs.filter((blog) => {
    const title = (blog.heading || blog.creditcardname || '').toLowerCase();
    const matchesText = title.includes(searchQuery.toLowerCase());

    const blogPriority = getBlogPriority(blog); // "1", "0", or ""
    const matchesPriority =
      selectedPriority === 'ALL' || blogPriority === selectedPriority;

    return matchesText && matchesPriority;
  });

  // helpers
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return isNaN(date) ? '' : date.toLocaleDateString();
  };
  const handleEdit = (id) => navigate(`/blogs/${id}`);
  const handlePreview = (id) =>
    navigate(`/PreviewBlog?slug=${encodeURIComponent(id)}`);
  const handleClickCreate = (type) => navigate(`/${type}/new`);

  /* ========================== Updated Softer Blue Theme ========================== */
  const styles = {
    page: {
      fontFamily:
        'Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',

      minHeight: '100vh',
      color: '#0f172a',
    },

    /* HERO */
    heroWrap: {
      background:
        'radial-gradient(900px 320px at 50% -20%, rgba(96,165,250,.35), rgba(37,99,235,.25) 45%, rgba(14,165,233,.18) 70%, transparent 80%), linear-gradient(180deg, #144b83 0%, #e2ecff 100%)',
      color: '#0f172a',
      padding: '56px 20px 28px',

      boxShadow: '0 6px 20px rgba(15,23,42,0.08)',
    },
    heroInner: { maxWidth: 1200, marginTop: '10px', margin: '0 auto' },
    heroTitle: {
      fontSize: '2.1rem',
      fontWeight: 800,
      letterSpacing: '.2px',
      margin: 0,
    },
    heroSub: {
      marginTop: 10,
      maxWidth: 740,
      color: '#334155',
      fontSize: '1rem',
      lineHeight: 1.65,
    },
    heroButtons: { display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 18 },
    heroBtn: {
      background:
        'linear-gradient(180deg, #93c5fd 0%, #3b82f6 50%, #2563eb 100%)', // deeper blue gradient
      border: '1px solid rgba(37,99,235,.4)',
      color: '#ffffff',
      fontWeight: 800,
      padding: '0.85rem 1.1rem',
      borderRadius: 40,
      cursor: 'pointer',
      boxShadow: '0 10px 25px rgba(37,99,235,.25)',
      transition: 'transform .15s ease, box-shadow .15s ease',
    },
    heroBtnHover: {
      transform: 'translateY(-1px)',
      boxShadow: '0 14px 32px rgba(37,99,235,.3)',
    },

    /* FILTER BAR */
    filterShell: {
      position: 'sticky',
      top: 0,
      zIndex: 10,
      background: 'rgba(255,255,255,0.92)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      borderBottom: '1px solid rgba(15,23,42,0.06)',
    },
    filterInner: {
      maxWidth: 1200,
      margin: '0 auto',
      padding: '12px 20px',
    },
    filterCard: {
      display: 'grid',
      gridTemplateColumns: '1.2fr 1fr 1fr 1fr',
      gap: 10,
    },
    search: {
      padding: '12px 14px',
      borderRadius: 10,
      border: '1px solid #dbe4f3',
      background: '#ffffff',
      color: '#0f172a',
      outline: 'none',
      fontSize: '.95rem',
    },
    select: {
      padding: '12px 14px',
      borderRadius: 10,
      border: '1px solid #dbe4f3',
      background: '#ffffff',
      color: '#0f172a',
      outline: 'none',
      fontSize: '.95rem',
    },

    /* CONTENT */
    contentWrap: {
      maxWidth: 1200,
      margin: '0 auto',
      marginTop: '40px',
      padding: '22px 20px 40px',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: 16,
    },
    card: {
      background: '#ffffff',
      border: '1px solid #dce4f2',
      borderRadius: 16,
      padding: 18,
      color: '#0f172a',
      cursor: 'pointer',
      transition:
        'transform .15s ease, box-shadow .15s ease, border-color .15s ease',
      boxShadow: '0 8px 22px rgba(15,23,42,.08)',
    },
    cardHover: {
      transform: 'translateY(-2px)',
      boxShadow: '0 14px 34px rgba(15,23,42,.12)',
      borderColor: '#c7d4eb',
    },
    titleRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 10,
    },
    title: {
      fontSize: '1rem',
      fontWeight: 800,
      color: '#0f172a',
      margin: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    badge: (kind) => ({
      padding: '0.18rem 0.55rem',
      borderRadius: 999,
      fontSize: '0.7rem',
      fontWeight: 800,
      color: kind === 'trending' ? '#fff' : '#334155',
      background: kind === 'trending' ? '#1c83dd' : '#e5edfa',
      border: '1px solid rgba(15,23,42,.07)',
    }),
    metaRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
      color: '#475569',
      fontSize: '.85rem',
      marginTop: 6,
    },
    iconBar: { display: 'flex', gap: 8 },
    iconBtn: {
      width: 34,
      height: 34,
      borderRadius: 10,
      border: '1px solid #dbe4f3',
      background: '#f8fbff',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 3px 8px rgba(15,23,42,.06)',
    },
    iconSvg: { width: 18, height: 18, color: '#334155' },

    empty: { color: '#64748b', padding: '1rem 0' },
  };

  return (
    <div style={styles.page}>
      {/* HERO */}
      <header style={styles.heroWrap}>
        <div style={styles.heroInner}>
          <h1 style={styles.heroTitle}>Write Smart Blogs</h1>
          <p style={styles.heroSub}>
            Create powerful content in minutes. Draft a static page, a full
            article, or a credit-card review — then preview and publish
            instantly.
          </p>
          <div style={styles.heroButtons}>
            <button
              style={styles.heroBtn}
              onMouseEnter={(e) =>
                Object.assign(e.currentTarget.style, styles.heroBtnHover)
              }
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = '';
                e.currentTarget.style.boxShadow = styles.heroBtn.boxShadow;
              }}
              onClick={() => handleClickCreate('static')}
            >
              ➕ Create Static
            </button>
            <button
              style={styles.heroBtn}
              onMouseEnter={(e) =>
                Object.assign(e.currentTarget.style, styles.heroBtnHover)
              }
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = '';
                e.currentTarget.style.boxShadow = styles.heroBtn.boxShadow;
              }}
              onClick={() => handleClickCreate('blog')}
            >
              ➕ Create Blog
            </button>
            <button
              style={styles.heroBtn}
              onMouseEnter={(e) =>
                Object.assign(e.currentTarget.style, styles.heroBtnHover)
              }
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = '';
                e.currentTarget.style.boxShadow = styles.heroBtn.boxShadow;
              }}
              onClick={() => handleClickCreate('cc')}
            >
              ➕ Create Credit Card Blog
            </button>
            <button
              style={styles.heroBtn}
              onMouseEnter={(e) =>
                Object.assign(e.currentTarget.style, styles.heroBtnHover)
              }
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = '';
                e.currentTarget.style.boxShadow = styles.heroBtn.boxShadow;
              }}
              onClick={() => navigate('/credit-cards')}
            >
              ➕ Create Credit Card
            </button>
            <button
              style={styles.heroBtn}
              onMouseEnter={(e) =>
                Object.assign(e.currentTarget.style, styles.heroBtnHover)
              }
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = '';
                e.currentTarget.style.boxShadow = styles.heroBtn.boxShadow;
              }}
              onClick={() => navigate('/credit-cards')}
            >
              ➕ Update Credit Card
            </button>
          </div>
        </div>
      </header>

      {/* FILTERS (sticky) */}
      <div style={styles.filterShell}>
        <div style={styles.filterInner}>
          <div style={styles.filterCard}>
            <input
              style={styles.search}
              type="text"
              placeholder="Search by title…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            <select
              style={styles.select}
              value={selectedPriority}
              onChange={(e) => setSelectedPriority(e.target.value)}
              title="Priority"
            >
              <option value="ALL">All Priorities</option>
              <option value="1">Trending</option>
              <option value="0">Non-trending</option>
            </select>

            <select
              style={styles.select}
              value={selectedSourceId}
              onChange={(e) => setSelectedSourceId(e.target.value)}
              title="Source"
            >
              <option value="ALL">All Sources</option>
              {sources.map((s) => (
                <option key={String(s.id)} value={String(s.id)}>
                  {s.name}
                </option>
              ))}
            </select>

            <select
              style={styles.select}
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              title="Blog Type"
            >
              <option value="ALL">All Types</option>
              <option value="blog">Blog</option>
              <option value="static">Static</option>
              <option value="cc_blog">Credit Card (cc_blog)</option>
            </select>
          </div>
        </div>
      </div>

      {/* CONTENT */}
      <main style={styles.contentWrap}>
        {loading && <p style={styles.empty}>Loading…</p>}

        <div style={styles.grid}>
          {filteredBlogs.length > 0
            ? filteredBlogs.map((blog) => {
                const priority = getBlogPriority(blog); // "1" | "0" | ""
                const isTrending = priority === '1';
                return (
                  <div
                    key={blog.id}
                    style={styles.card}
                    onMouseEnter={(e) =>
                      Object.assign(e.currentTarget.style, styles.cardHover)
                    }
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = '';
                      e.currentTarget.style.boxShadow = styles.card.boxShadow;
                      e.currentTarget.style.borderColor = '#dce4f2';
                    }}
                    onClick={() => handleEdit(blog.id)}
                    title="Open editor"
                  >
                    <div style={styles.titleRow}>
                      <h4 style={styles.title}>
                        {blog.heading || blog.creditcardname || 'Untitled'}
                      </h4>
                      {priority !== '' && (
                        <span
                          style={styles.badge(isTrending ? 'trending' : 'non')}
                        >
                          {isTrending ? 'Trending' : 'Non-trending'}
                        </span>
                      )}
                    </div>

                    <div style={styles.metaRow}>
                      <span>{formatDate(blog.updated_at) || 'No date'}</span>
                      <div
                        style={styles.iconBar}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          style={styles.iconBtn}
                          title="Preview"
                          aria-label="Preview"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePreview(blog.id);
                          }}
                        >
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            style={styles.iconSvg}
                          >
                            <path
                              d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z"
                              strokeWidth="2"
                            />
                            <circle cx="12" cy="12" r="3" strokeWidth="2" />
                          </svg>
                        </button>

                        <button
                          style={styles.iconBtn}
                          title="Edit"
                          aria-label="Edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(blog.id);
                          }}
                        >
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            style={styles.iconSvg}
                          >
                            <path d="M12 20h9" strokeWidth="2" />
                            <path
                              d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4L16.5 3.5Z"
                              strokeWidth="2"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            : !loading && <p style={styles.empty}>No blogs available.</p>}
        </div>
      </main>
    </div>
  );
}
